import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

export default function InterviewResults() {
  const { state } = useLocation();
  const navigate = useNavigate();

  if (!state) {
    return (
      <div style={styles.stage}>
        <div style={styles.card}>
          <p style={styles.title}>No results to show yet.</p>
          <button style={styles.cta} onClick={() => navigate("/ai-interview/setup")}>
            Start an interview ▸
          </button>
        </div>
      </div>
    );
  }

  const { evaluation, error, role, type } = state;

  if (error || !evaluation) {
    return (
      <div style={styles.stage}>
        <div style={styles.card}>
          <p style={styles.eyebrow}>SCENE CLOSED</p>
          <p style={styles.title}>{error || "Something went wrong scoring this interview."}</p>
          <button style={styles.cta} onClick={() => navigate("/ai-interview/setup")}>
            Try another interview ▸
          </button>
        </div>
      </div>
    );
  }

  const { overall, scores = {}, summary, strengths = [], weaknesses = [], improvements = [], recommendation } = evaluation;

  return (
    <div style={styles.stage}>
      <div style={styles.glow} />
      <div style={styles.wrap}>
        <p style={styles.eyebrow}>CURTAIN CALL · {type === "hr" ? "HR ROUND" : "TECHNICAL ROUND"} · {role}</p>

        <div style={styles.scoreRow}>
          <div style={styles.scoreCircle}>
            <span style={styles.scoreNumber}>{overall}</span>
            <span style={styles.scoreOutOf}>/ 100</span>
          </div>
          <div style={styles.recommendationBox}>
            <p style={styles.recLabel}>RECOMMENDATION</p>
            <p style={styles.recText}>{recommendation || "—"}</p>
          </div>
        </div>

        {summary && <p style={styles.summary}>{summary}</p>}

        {Object.keys(scores).length > 0 && (
          <div style={styles.breakdown}>
            {Object.entries(scores).map(([label, val]) => (
              <div key={label} style={styles.breakdownRow}>
                <span style={styles.breakdownLabel}>{label}</span>
                <div style={styles.barTrack}>
                  <div style={{ ...styles.barFill, width: `${val}%` }} />
                </div>
                <span style={styles.breakdownValue}>{val}</span>
              </div>
            ))}
          </div>
        )}

        <div style={styles.columns}>
          <div style={styles.col}>
            <p style={styles.colTitle}>Strengths</p>
            {strengths.length ? strengths.map((s, i) => (
              <p key={i} style={styles.goodItem}>+ {s}</p>
            )) : <p style={styles.emptyItem}>None recorded.</p>}
          </div>
          <div style={styles.col}>
            <p style={styles.colTitle}>Areas to improve</p>
            {weaknesses.length ? weaknesses.map((w, i) => (
              <p key={i} style={styles.badItem}>– {w}</p>
            )) : <p style={styles.emptyItem}>None recorded.</p>}
          </div>
        </div>

        {improvements.length > 0 && (
          <div style={styles.improveBox}>
            <p style={styles.colTitle}>Next time, try</p>
            {improvements.map((imp, i) => (
              <p key={i} style={styles.improveItem}>{i + 1}. {imp}</p>
            ))}
          </div>
        )}

        <button style={styles.cta} onClick={() => navigate("/ai-interview/setup")}>
          Rehearse another scene ▸
        </button>
      </div>
    </div>
  );
}

const styles = {
  stage: {
    minHeight: "100vh",
    background: "#10151F",
    position: "relative",
    overflow: "hidden",
    fontFamily: "'IBM Plex Sans', sans-serif",
    padding: "48px 20px",
    boxSizing: "border-box",
  },
  glow: {
    position: "absolute",
    top: "-20%",
    left: "50%",
    transform: "translateX(-50%)",
    width: "800px",
    height: "800px",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(78,156,124,0.18) 0%, rgba(78,156,124,0) 70%)",
    pointerEvents: "none",
  },
  wrap: {
    position: "relative",
    maxWidth: "760px",
    margin: "0 auto",
  },
  eyebrow: {
    color: "#C99A4B",
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: "11px",
    letterSpacing: "2px",
    marginBottom: "22px",
  },
  scoreRow: {
    display: "flex",
    alignItems: "center",
    gap: "28px",
    marginBottom: "22px",
  },
  scoreCircle: {
    width: "110px",
    height: "110px",
    borderRadius: "50%",
    border: "3px solid #4E9C7C",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  scoreNumber: {
    fontFamily: "'Fraunces', serif",
    color: "#EFEAE1",
    fontSize: "34px",
    fontWeight: 700,
    lineHeight: 1,
  },
  scoreOutOf: {
    color: "#6B7488",
    fontSize: "11px",
    marginTop: "2px",
  },
  recommendationBox: {
    background: "#161C29",
    border: "1px solid #232B3B",
    borderRadius: "14px",
    padding: "16px 20px",
    flex: 1,
  },
  recLabel: {
    color: "#8A93A6",
    fontSize: "10px",
    letterSpacing: "1.5px",
    fontFamily: "'IBM Plex Mono', monospace",
    marginBottom: "6px",
  },
  recText: {
    color: "#EFEAE1",
    fontSize: "15px",
    fontWeight: 600,
    margin: 0,
  },
  summary: {
    color: "#B9C0CE",
    fontSize: "14px",
    lineHeight: 1.6,
    marginBottom: "26px",
  },
  breakdown: {
    background: "#161C29",
    border: "1px solid #232B3B",
    borderRadius: "14px",
    padding: "18px 22px",
    marginBottom: "26px",
  },
  breakdownRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "12px",
  },
  breakdownLabel: {
    color: "#8A93A6",
    fontSize: "12px",
    width: "140px",
    flexShrink: 0,
  },
  barTrack: {
    flex: 1,
    height: "6px",
    background: "#232B3B",
    borderRadius: "999px",
    overflow: "hidden",
  },
  barFill: {
    height: "100%",
    background: "#C99A4B",
    borderRadius: "999px",
  },
  breakdownValue: {
    color: "#EFEAE1",
    fontSize: "12px",
    width: "28px",
    textAlign: "right",
    fontFamily: "'IBM Plex Mono', monospace",
  },
  columns: {
    display: "flex",
    gap: "20px",
    marginBottom: "24px",
  },
  col: {
    flex: 1,
    background: "#161C29",
    border: "1px solid #232B3B",
    borderRadius: "14px",
    padding: "18px 20px",
  },
  colTitle: {
    color: "#8A93A6",
    fontSize: "11px",
    letterSpacing: "1px",
    marginBottom: "12px",
    fontFamily: "'IBM Plex Mono', monospace",
  },
  goodItem: {
    color: "#4E9C7C",
    fontSize: "13px",
    marginBottom: "8px",
    lineHeight: 1.5,
  },
  badItem: {
    color: "#D9705A",
    fontSize: "13px",
    marginBottom: "8px",
    lineHeight: 1.5,
  },
  emptyItem: {
    color: "#4B5566",
    fontSize: "13px",
  },
  improveBox: {
    background: "#161C29",
    border: "1px solid #232B3B",
    borderRadius: "14px",
    padding: "18px 20px",
    marginBottom: "30px",
  },
  improveItem: {
    color: "#B9C0CE",
    fontSize: "13px",
    marginBottom: "8px",
    lineHeight: 1.5,
  },
  cta: {
    background: "#C99A4B",
    color: "#10151F",
    border: "none",
    borderRadius: "10px",
    padding: "14px 24px",
    fontSize: "13px",
    fontWeight: 700,
    cursor: "pointer",
  },
  card: {
    background: "#161C29",
    border: "1px solid #232B3B",
    borderRadius: "18px",
    padding: "30px",
    margin: "80px auto",
    textAlign: "center",
    maxWidth: "480px",
  },
  title: {
    color: "#EFEAE1",
    marginBottom: "16px",
  },
};
